import { AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { WorkspaceDatabaseKeys } from '@database/workspace';
import { FormActionTypeEnum, PrimaryKeyValueModel } from '@shared';
import { combineLatest, first, map, Observable, of } from 'rxjs';
import { ValidationKeys } from '../models/validation-keys';
import { QueryParamsProvider } from '@domain/routing';
import { DataByTableTypeProvider, SamplesTableProvider } from '@domain/business-layer';
import { ValidationsBuilderModel } from '../models';

export class UniqueStandardNumberBuilder {
	private validation: AsyncValidatorFn;
	private validationsMessages: string[] = [];

	public constructor(
		private entityName: string,
		private childTableName: string,
		private projectNumber: string,
		private columnName: string,
		private formActionType: FormActionTypeEnum,
		private primaryKeys: string[],
		private queryParamsProvider: QueryParamsProvider,
		private dataByTableTypeProvider: DataByTableTypeProvider,
		private samplesTableProvider: SamplesTableProvider
	) {}

	public validate(): ValidationsBuilderModel | null {
		if (this.columnName === WorkspaceDatabaseKeys.columns.standardNumber) {
			this.validation = this.uniqueStandardKeyValidation();
			this.validationsMessages.push(ValidationKeys.validationRules.uniqueStandardNumberRule);
			this.validationsMessages.push(ValidationKeys.validationRules.uniqueDeletedStandardNumberRule);

			return { validation: this.validation, messages: this.validationsMessages };
		}

		return null;
	}

	private uniqueStandardKeyValidation(): AsyncValidatorFn {
		return (control: AbstractControl): Observable<ValidationErrors | null> => {
			if (control?.value) {
				const group = control.parent;
				const primaryKeyValue: PrimaryKeyValueModel[] = this.primaryKeys.map(
					(pk: string) => ({ primaryKey: pk, primaryKeyValue: group?.get(pk).value }),
					[]
				);

				let excludedId = 0;

				if (this.formActionType === FormActionTypeEnum.edit) {
					excludedId = Number(
						this.queryParamsProvider.childEntryId
							? this.queryParamsProvider.childEntryId
							: this.queryParamsProvider.entryId
					);
				}

				return combineLatest([
					this.dataByTableTypeProvider.isStandardsTablePropertyValueUnique$(
						this.entityName,
						this.childTableName,
						this.samplesTableProvider.getSamplesTable,
						this.projectNumber,
						primaryKeyValue,
						WorkspaceDatabaseKeys.columns.sampleNumber,
						this.columnName,
						control.value,
						excludedId,
						false
					),
					this.dataByTableTypeProvider.isStandardsTablePropertyValueUnique$(
						this.entityName,
						this.childTableName,
						this.samplesTableProvider.getSamplesTable,
						this.projectNumber,
						primaryKeyValue,
						WorkspaceDatabaseKeys.columns.sampleNumber,
						this.columnName,
						control.value,
						excludedId,
						true
					)
				]).pipe(
					map(([isUniqueStandardNumber, isUniqueDeletedStandardNumber]) => {
						if (!isUniqueDeletedStandardNumber) {
							return { [ValidationKeys.validationRules.uniqueDeletedStandardNumberRule]: true };
						}

						if (!isUniqueStandardNumber) {
							return { [ValidationKeys.validationRules.uniqueStandardNumberRule]: true };
						}

						return null;
					}),
					first()
				);
			}
			return of(null);
		};
	}
}
